import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { takeUntil, map, distinctUntilChanged } from 'rxjs/operators';

import { Product } from 'src/app/domain/Product';
import { Cart } from 'src/app/domain/Cart';
import { Order } from 'src/app/domain/Order';
import { Voucher } from 'src/app/domain/Voucher';
import { OrderConfig, ShippingCountry, TaxCountry } from 'src/app/domain/OrderConfig';
import { ProductInfo } from 'src/app/domain/OrderDetail';
import { Country } from 'src/app/domain/Country';

import { StorageService } from './storage.service';

import { ProductRetiredComponent } from 'src/app/shared/modals/product-retired/product-retired.component';

import { cloneDeep, isEqual } from 'lodash-es';

export interface CartCheckResponse {
  country: Country;
  products: Product[];
  productsRetired: ProductNotAllowed[];
  productsNotAllowed: ProductNotAllowed[];
  productCartFeatured?: Product;
  productsCost: number;
  productsDiscount?: number;
  taxCost: number;
  taxCountry: TaxCountry;
  taxCountryShipping: TaxCountry;
  shippingCostCountry: ShippingCountry;
  shippingBaseCost: number;
  shippingTaxCost: number;
  fullShippingCost: number;
  amountForFreeShipping?: number;
  missingAmountForFreeShipping?: number;
  subtotalCost: number;
  totalCost: number;
  vouchersCheck?: {
    vouchers: Voucher[];
    baseDiscount: number;
    shippingBaseDiscount: number;
    freeShippingCost: boolean;
  };
  plasticTaxCost?: number;
}

export interface ProductNotAllowed {
  reason: string;
  product: Product;
}

export interface OrderCreateResponse {
  orderId: string;
  invoiceId?: string;
  invoiceUrl?: string;
  apmInvoiceUrl?: string;
  transactionId?: string;
  bypass?: boolean;
  paymentMethod?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly http = inject(HttpClient);
  private readonly modalService = inject(NgbModal);
  private readonly storageService = inject(StorageService);

  public orderConfigSubject$: BehaviorSubject<OrderConfig>;
  public orderConfig$: Observable<OrderConfig>;

  private cartSubject$: BehaviorSubject<Cart>;
  public cart$: Observable<Cart>;

  private cartClone: Cart;

  calls = new Subject();
  public orderSubject$: BehaviorSubject<Order>;
  public order$: Observable<Order>;

  public cartNotifications$: Observable<number>;

  constructor() {
    this.initOrderConfig();
    this.initCart();
    this.initOrder();

    this.orderConfigSubject$.subscribe((orderConfig) => {
      this.storageService.setItem('orderConfig', orderConfig);
    });

    this.cartSubject$.subscribe((cart) => {
      const mustRequestCartCheck =
        this.cartClone === undefined ||
        !isEqual(this.cartClone.requestProducts, cart.requestProducts) ||
        !isEqual(this.cartClone.vouchers, cart.vouchers) ||
        !isEqual(this.cartClone.customerAddress, cart.customerAddress) ||
        !isEqual(this.cartClone.postalCode, cart.postalCode) ||
        !isEqual(this.cartClone.products, cart.products)||
        !isEqual(this.cartClone.productCartFeatured, cart.productCartFeatured) ||
        !isEqual(this.cartClone.productCartFeaturedAddedId, cart.productCartFeaturedAddedId) ||
        !isEqual(this.cartClone.postalCode, cart.postalCode)

      // Codificamos la imagen a base64 para que se pueda guardar en el localstorage
      if (cart.requestProducts) {
        cart.requestProducts.forEach((x) => {
          if (x?.attachedFile) {
            this.getBase64(x.attachedFile).then((b64) => {
              x.attachedFileBase64 = b64;
            });
          }
        });
      }

      this.storageService.setItem('cart', cart);
      this.cartClone = cloneDeep(cart);

      if (!mustRequestCartCheck) {
        cart.loading = false;
        return;
      }

      if (!this.canRequestCartCheck()) {
        cart.loading = false;
        return;
      }

      cart.loading = true;
      this.calls.next(true);

      this.cartCheck()
        .pipe(
          // Debounce
          takeUntil(this.calls)
        )
        .subscribe((x: CartCheckResponse) => {
          // Informamos si se han retirado productos del carrito
          const productsRetired = x.productsRetired ? x.productsRetired.map((product) => product.product) : [];
          // Productos Retirados
          if (productsRetired && productsRetired.length > 0) {
            const modalRef = this.modalService.open(ProductRetiredComponent);
            modalRef.componentInstance.productsRetired = x.productsRetired;
            productsRetired.forEach((productRetired) => {
              this.removeProductFromCart(productRetired).then();
            });
          }
          // Packs
          cart.requestProducts?.forEach((cartProduct) => {
            const checkProduct = x.products?.find(
              (cartCheckProduct) => cartCheckProduct.id === cartProduct.id || cartCheckProduct.pack === cartProduct.pack
            );
            if (!checkProduct) {
              cart.requestProducts = cart.requestProducts.filter(
                (requestProduct) => cartProduct.id !== requestProduct.id || cartProduct.pack !== requestProduct.pack
              );
            }
          });
          // Vouchers, retiramos los vouchers no aplicables para que no se detecten como incluidos en el cart
          const validVoucherCodes = x.vouchersCheck?.vouchers
            ? x.vouchersCheck.vouchers.filter((voucher) => voucher.isValid).map((voucher) => voucher.code)
            : [];

          cart.vouchers = cart.vouchers?.filter((voucherCode) => validVoucherCodes.includes(voucherCode));
          const order: Order = {
            order: {
              productsCost: x.productsCost,
              productsDiscount: x.productsDiscount,
              subtotalCost: x?.subtotalCost,
              totalCost: x.totalCost,
              taxCost: x.taxCost,
              shippingCostWithTaxes: x.shippingBaseCost + x.shippingTaxCost,
              shippingBaseCost: x.shippingBaseCost,
              shippingTaxCost: x.shippingTaxCost,
              fullShippingCost: x.fullShippingCost,
              amountForFreeShipping: x.amountForFreeShipping,
              missingAmountForFreeShipping: x.missingAmountForFreeShipping,
              plasticTaxCost: x.plasticTaxCost,
            },
            products: cart.products,
            vouchersCheck: x.vouchersCheck ? x.vouchersCheck : null,
          };
          this.orderSubject$.next(order);
          this.orderConfigSubject$.next({
            taxCountry: x.taxCountry,
            taxCountryShipping: x.taxCountryShipping,
            shippingCost: x.shippingCostCountry,
            country: x.country,
          });
          cart.loading = false;
          if (cart.products !== x.products) {
            cart.products = x.products;
          }
          cart.productCartFeatured = x.productCartFeatured ?? null;
          this.cartSubject$.next(cart);
        });
    });

    this.orderSubject$.subscribe((order) => {
      this.storageService.setItem('order', order);
    });
  }

  public cartCheck(voucherCode?: string): Observable<any> {
    if (!this.canRequestCartCheck()) {
      return EMPTY;
    }

    const cart = this.cartSubject$.getValue();
    const parsedCart: Cart = { products: [], vouchers: [] };
    const formData: FormData = new FormData();
    cart.requestProducts?.forEach((p) => {
      const parseProduct = p.pack
        ? { id: p.id, qt: p.qt, pack: p.pack }
        : { id: p.id, qt: p.qt, designId: p.designId, customImage: p.customImage };
      parsedCart.products.push(parseProduct);
      if (p.attachedFile) {
        formData.append('products-id:' + p.id + '=attachment', p.attachedFile);
      }
    });

    const params: any = {
      products: parsedCart.products,
      vouchers: cart?.vouchers,
      customerAddress: cart?.customerAddress,
      postalCode: cart?.postalCode,
    };

    if (voucherCode) {
      if (cart.vouchers) {
        const currentOrder = this.orderSubject$.getValue();
        const cartVouchers = [];
        cart.vouchers.forEach((cartVoucherCode) => {
          let addVoucher = true;
          currentOrder.vouchersCheck?.vouchers.forEach((orderVoucher) => {
            if (cartVoucherCode === orderVoucher.code && !orderVoucher.isValid) {
              addVoucher = false;
            }
          });
          if (addVoucher) {
            cartVouchers.push(cartVoucherCode);
          }
        });
        params.vouchers = [...cartVouchers, voucherCode];
      } else {
        params.vouchers = [voucherCode];
      }
    }
    formData.append('parameters', JSON.stringify(params));

    return this.http.post('order/cart/check', formData);
  }

  public currentCartCheckVoucher(voucherCode: string) {
    const cart = this.cartSubject$.getValue();
    return cart.vouchers?.some((x) => x === voucherCode);
  }

  public orderCreate(
    customerAddressId: number,
    customerBillingAddressId: number,
    paymentMethod: string,
    bankTransferFile: File,
    imageAttachment: File,
    notes: string
  ): Observable<any> {
    const cart = this.cartSubject$.getValue();
    const formData: FormData = new FormData();
    const parsedCart: Cart = { products: [], vouchers: [] };
    cart.requestProducts.forEach((cartProduct) => {
      const parseProduct = cartProduct.pack
        ? { id: cartProduct.id, qt: cartProduct.qt, pack: cartProduct.pack }
        : {
            id: cartProduct.id,
            qt: cartProduct.qt,
            designId: cartProduct.designId,
            customImage: cartProduct.customImage,
          };
      parsedCart.products.push(parseProduct);
      if (cartProduct.attachedFile) {
        formData.append('products-id:' + cartProduct.id + '=attachment', cartProduct.attachedFile);
      }
    });

    const parameters = {
      order: { customerAddress: customerAddressId, customerBillingAddress: customerBillingAddressId, notes },
      products: parsedCart.products,
      vouchers: cart.vouchers ? cart.vouchers : [],
      paymentMethod,
    };
    formData.append('parameters', JSON.stringify(parameters));
    if (bankTransferFile instanceof File) {
      formData.append('receipt', bankTransferFile, bankTransferFile.name);
    }
    if (imageAttachment instanceof File) {
      formData.append('attachment', imageAttachment, imageAttachment.name);
    }
    return this.http.post('order/create', formData);
  }

  public guestOrderCreate(
    order: any,
    customerRegistration: boolean,
    paymentMethod: string,
    bankTransferFile: File,
    imageAttachment: File,
  ): Observable<any> {
    const cart = this.cartSubject$.getValue();
    const parsedCart: Cart = { products: [], vouchers: [] };
    const formData: FormData = new FormData();
    cart.requestProducts.forEach((cartProduct) => {
      const parseProduct = cartProduct.pack
        ? { id: cartProduct.id, qt: cartProduct.qt, pack: cartProduct.pack }
        : {
          id: cartProduct.id,
          qt: cartProduct.qt,
          designId: cartProduct.designId,
          customImage: cartProduct.customImage,
        };
      parsedCart.products.push(parseProduct);
      if (cartProduct.attachedFile) {
        formData.append('products-id:' + cartProduct.id + '=attachment', cartProduct.attachedFile);
      }
    });
    const parameters = {
      order,
      products: parsedCart.products,
      vouchers: cart.vouchers ? cart.vouchers : [],
      customerRegistration,
      paymentMethod
    };
    formData.append('parameters', JSON.stringify(parameters));
    if (bankTransferFile instanceof File) {
      formData.append('receipt', bankTransferFile, bankTransferFile.name);
    }
    if (imageAttachment instanceof File) {
      formData.append('attachment', imageAttachment, imageAttachment.name);
    }
    return this.http.post('order/guest-create', formData);
  }

  public downloadInvoice(id: number, email?: string, channelOrderNumber?: string): Observable<Blob> {
    const params: any = {};
    if (email) {
      params.email = email;
    }
    if (channelOrderNumber) {
      params.channelOrderNumber = channelOrderNumber;
    }

    return this.http.get('order/invoice-download/' + id, {
      responseType: 'blob',
      params,
    });
  }


  sendCardToken(invoiceId: string, cardId: string): Promise<{ transactionId: string | null }> {
    const url = 'order/card-token';
    return firstValueFrom(
      this.http.post<{ transactionId: string | null }>(url, { invoiceId, cardId })
    );
  }

  public processGatewayToken(invoiceId: string, gatewayToken: string): Observable<any> {
    return this.http.post('order/gateway-token', {
      invoiceId,
      gatewayToken,
    });
  }

  get currentOrderConfig(): OrderConfig {
    return this.storageService.getItem<OrderConfig>('orderConfig');
  }

  dataURLtoFile(dataurl: string, filename: string) {
    let arr: any;
    arr = dataurl.split(',');
    let mime: any;
    mime = arr[0].match(/:(.*?);/)[1];
    let bstr: string;
    bstr = atob(arr[1]);
    let n = bstr.length;
    let u8arr: Uint8Array;
    u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  get currentCart() {
    const jsonCart = this.storageService.getItem<any>('cart');
    if (jsonCart) {
      jsonCart.requestProducts?.forEach((p: { attachedFile: File; attachedFileBase64: string }) => {
        if (p?.attachedFile) {
          p.attachedFile = this.dataURLtoFile(p.attachedFileBase64, 'image');
        }
      });
    }
    return jsonCart;
  }

  get currentOrder(): Order {
    return this.storageService.getItem<Order>('order');
  }

  private initOrderConfig() {
    if (Boolean(this.currentOrderConfig) === false) {
      this.orderConfigSubject$ = new BehaviorSubject(null);
      this.orderConfig$ = this.orderConfigSubject$.asObservable();
      return;
    }
    this.orderConfigSubject$ = new BehaviorSubject(this.currentOrderConfig);
    this.orderConfig$ = this.orderConfigSubject$.asObservable();
  }

  private initCart() {
    const initialCart = this.currentCart ?? cloneDeep(this.getDefaultCart());

    this.cartSubject$ = new BehaviorSubject(initialCart);
    this.cart$ = this.cartSubject$.asObservable();

    this.cartNotifications$ = this.cart$.pipe(
      map((cart) => this.getCartNotifications(cart)),
      distinctUntilChanged()
    );
  }

  private getCartNotifications(cart: Cart): number {
    const sourceProducts = cart?.products?.length ? cart.products : cart?.requestProducts ?? [];

    return sourceProducts.reduce((accumulator, currentProduct) => {
      const units = currentProduct.units ?? currentProduct.qt ?? 0;
      const packUnits = currentProduct.packUnits ?? 1;

      return accumulator + (units / packUnits);
    }, 0);
  }

  private initOrder() {
    if (Boolean(this.currentOrder) === false) {
      this.orderSubject$ = new BehaviorSubject(this.getDefaultOrder());
      this.order$ = this.orderSubject$.asObservable();
      return;
    }
    this.orderSubject$ = new BehaviorSubject(this.currentOrder);
    this.order$ = this.orderSubject$.asObservable();
  }

  private getCountryFromStorage(): { code: string; locale: string } | null {
    const params = this.storageService.getItem<any>('params');

    if (!params?.country?.code || !params?.country?.locale) {
      return null;
    }

    return {
      code: params.country.code,
      locale: params.country.locale,
    };
  }

  private canRequestCartCheck(): boolean {
    return !!this.getCountryFromStorage();
  }

  addOrderConfig() {
    if (!this.canRequestCartCheck()) {
      return;
    }

    this.cartCheck().subscribe((x: CartCheckResponse) => {
      this.orderConfigSubject$.next({
        taxCountry: x.taxCountry,
        taxCountryShipping: x.taxCountryShipping,
        shippingCost: x.fullShippingCost,
        country: x.country,
      });
    });
  }

  /**
   * Rehidrata cart / order / orderConfig desde StorageService
   */
  public reloadFromStorage(): void {
    const storedOrderConfig = this.currentOrderConfig ?? null;
    const storedCart = this.currentCart ?? this.getDefaultCart();
    const storedOrder = this.currentOrder ?? this.getDefaultOrder();
    this.cartClone = cloneDeep(storedCart);

    if (this.orderConfigSubject$) {
      this.orderConfigSubject$.next(storedOrderConfig);
    }

    if (this.cartSubject$) {
      this.cartSubject$.next(storedCart);
    }
    if (this.orderSubject$) {
      this.orderSubject$.next(storedOrder);
    }
  }
  getBase64(file: Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  addProductToCart(product: Product, qt: number, file?: File, designId?: string, customImage?: string) {
    let newProduct = true;
    const cart = this.cartSubject$.getValue();
    if (!cart.requestProducts) {
      cart.requestProducts = [];
    }
    cart.requestProducts.forEach((cartProduct) => {
      if (cartProduct.id === product.id && cartProduct.pack === product.pack) {
        newProduct = false;
        cartProduct.qt += qt;
        cartProduct.attachedFile = file;
        cartProduct.designId = designId;
        cartProduct.customImage = customImage;
      }
    });
    if (newProduct) {
      cart.requestProducts.push({ ...product, ...{ qt, attachedFile: file, designId, customImage } });
    }
    this.cartSubject$.next(cart);
  }

  addProductCartFeaturedToCart(product: Product, qt: number, file?: File, designId?: string, customImage?: string) {
    const cart = this.cartSubject$.getValue();

    cart.productCartFeaturedAddedId = product.id;
    this.cartSubject$.next(cart);

    this.addProductToCart(product, qt, file, designId, customImage);
  }

  private syncProductCartFeatured(cart: Cart, removedProduct?: Product): void {
    const addedFeaturedId = cart.productCartFeaturedAddedId;

    if (!cart.requestProducts?.length) {
      cart.productCartFeaturedAddedId = null;
      return;
    }

    if (!addedFeaturedId || !removedProduct) {
      return;
    }

    if (removedProduct.id !== addedFeaturedId) {
      return;
    }

    const stillExists = cart.requestProducts.some(
      (p) => p.id === addedFeaturedId
    );

    if (!stillExists) {
      cart.productCartFeaturedAddedId = null;
    }
  }

  addPackToCart(products: Product[]) {
    const cart = this.cartSubject$.getValue();
    if (!cart.requestProducts) {
      cart.requestProducts = [];
    }

    products.forEach((product) => {
      let newProduct = true;
      cart.requestProducts.forEach((cartProduct) => {
        if (cartProduct.id === product.id && cartProduct.pack === product.pack) {
          newProduct = false;
          cartProduct.qt += product.packUnits;
        }
      });
      if (newProduct) {
        cart.requestProducts.push({
          ...product,
          ...{ qt: product.packUnits, pack: product.pack, attachedFile: null, designId: null, customImage: null },
        });
      }
    });

    this.cartSubject$.next(cart);
  }

  async removeProductFromCart(product: Product, qt?: number) {
    let removeProduct = false;
    let cart = this.cartSubject$.getValue();
    if (qt !== null && qt !== undefined) {
      cart.requestProducts.forEach((cartProduct) => {
        if (cartProduct.id === product.id && cartProduct.pack === product.pack) {
          cartProduct.qt -= qt;
          if (cartProduct.qt <= 0) {
            removeProduct = true;
          }
        }
      });
    }

    if (qt === null || qt === undefined || removeProduct) {
      cart = this.checkPacks(product, cart);
      cart.requestProducts = cart.requestProducts.filter((cartProduct) => {
        return cartProduct.id !== product.id || cartProduct.pack !== product.pack;
      });
    }
    this.syncProductCartFeatured(cart, product);
    this.cartSubject$.next(cart);
  }

  removeVoucherFromCart(voucher: Voucher) {
    const cart = this.cartSubject$.getValue();
    cart.vouchers = cart.vouchers.filter((v) => v !== voucher.code);
    this.cartSubject$.next(cart);
  }

  async updateQtProductFromCart(product: Product, qt: number, cartCard = true) {
    let removeProduct = false;
    let cart: Cart;
    cart = this.cartSubject$.getValue();

    if (qt !== null) {
      cart.requestProducts.forEach((cartProduct) => {
        if (cartProduct.id === product.id && cartProduct.pack === product.pack) {
          if (product.procurementCreditsSfManaged && cartCard) {
            // Buscamos si hay más productos con este id en products para agregar su qt
            cartProduct.qt = 0;
            cart.products
              .filter((sfmp) => sfmp.id === product.id)
              .forEach((sfmp) => {
                // Estamos en el mismo producto
                if (sfmp.units === product.units) {
                  cartProduct.qt += qt;
                } else {
                  cartProduct.qt += sfmp.units;
                }
              });
          } else {
            cartProduct.qt = qt;
          }
          if (cartProduct.qt <= 0) {
            removeProduct = true;
          }
        }
      });
    }

    if (qt === null || removeProduct) {
      cart.requestProducts = cart.requestProducts.filter((cartProduct) => {
        return cartProduct.id !== product.id || cartProduct.pack !== product.pack;
      });
    }

    this.cartSubject$.next(cart);
  }

  addCustomerAddressToCart(customerAddress: number) {
    const cart = this.cartSubject$.getValue();
    cart.customerAddress = customerAddress;
    this.cartSubject$.next(cart);
  }

  addPostalCodeToCart(postalCode: string) {
    const cart = this.cartSubject$.getValue();
    cart.postalCode = postalCode;
    this.cartSubject$.next(cart);
  }

  addVoucherToCart(voucher: string) {
    const cart = this.cartSubject$.getValue();

    const currentOrder = this.orderSubject$.getValue();

    const newVouchers = [];

    cart.vouchers?.forEach((voucherCode) => {
      let addVoucher = true;
      currentOrder.vouchersCheck?.vouchers.forEach((orderVoucher) => {
        if (voucherCode === voucher || (voucherCode === orderVoucher.code && !orderVoucher.isValid)) {
          addVoucher = false;
        }
      });
      if (addVoucher) {
        newVouchers.push(voucherCode);
      }
    });

    newVouchers.push(voucher);

    cart.vouchers = newVouchers;
    this.cartSubject$.next(cart);
  }

  checkCart() {
    const cart = this.cartSubject$.getValue();
    this.cartSubject$.next(cart);
  }

  checkPacks(product: Product, cart: Cart) {
    if (product.pack) {
      cart.requestProducts.forEach((cartProduct, index) => {
        if (cartProduct.pack === product.pack) {
          // Si se elimina un producto adiccional se quita la misma cantidad del producto principal
          if (product.principalProductPack && product.principalProductPack === cartProduct.id) {
            const packUnitsPrinciapProduct = cart.products.find((p) => p.id === cartProduct.id).packUnits;
            const qtToDelete = cartProduct.qt - (product.units / product.packUnits) * packUnitsPrinciapProduct;
            this.updateQtProductFromCart(cartProduct, qtToDelete, false).then();

            // Si se elimina el producto principal eliminamos todos sus productos adiccionales del carrito
          } else if (!product.principalProductPack && cartProduct.id !== product.id) {
            delete cart.requestProducts[index];
          }
        }
      });
    }
    return cart;
  }

  clearCart() {
    delete this.cartClone;
    this.cartSubject$.next(this.getDefaultCart());
  }

  clearOrder() {
    this.orderSubject$.next(this.getDefaultOrder());
  }

  getDefaultCart(): Cart {
    return { products: [], requestProducts: [], productCartFeatured: null, productCartFeaturedAddedId: null };
  }

  getDefaultOrder(): Order {
    return { order: {}, products: [] };
  }

  /**
   * Función que organiza una lista de productos por dropshipper
   */
  organiceProductsByDropshipping(productsList: (Product | ProductInfo)[]) {
    const products: any = [];

    if (productsList && productsList.length > 0) {
      // Clasificación de productos
      const warehouseProducts = productsList.filter((p) => !p.dropshippingSku && !p.hasAttachments && !p.designId);
      const dropshippingProducts = productsList.filter((p) => p.dropshippingSku && !p.hasAttachments && !p.designId);
      const customizableProducts = productsList.filter((p) => p.hasAttachments || p.designId);

      // Productos de almacén
      if (warehouseProducts.length) {
        products.push({
          products: warehouseProducts,
          deliveryTime: warehouseProducts[0].deliveryTime,
          trackingStatus: warehouseProducts[0].trackingStatus,
          trackingId: warehouseProducts[0].trackingId,
          trackingUrl: warehouseProducts[0].trackingUrl,
        });
      }

      // Productos de dropshipping
      const dropshippingMap = new Map();
      dropshippingProducts.forEach((product: Product) => {
        const companyId = product.dropshippingCompany.id;
        if (!dropshippingMap.has(companyId)) {
          dropshippingMap.set(companyId, {
            products: [],
            deliveryTime: product.deliveryTime,
            trackingStatus: product.trackingStatus,
            trackingId: product.trackingId,
            trackingUrl: product.trackingUrl,
          });
        }
        dropshippingMap.get(companyId).products.push(product);
      });

      dropshippingMap.forEach((value) => products.push(value));

      // Productos personalizables
      customizableProducts.forEach((product: Product) => {
        products.push({
          products: [product],
          deliveryTime: product.deliveryTime,
          trackingStatus: product.trackingStatus,
          trackingId: product.trackingId,
          trackingUrl: product.trackingUrl,
        });
      });
    }

    return products;
  }

  /**
   * Comprueba si está el producto customizado en el carrito
   * @param product El producto a buscar
   */
  checkCustomizedProductInCart(product: Product) {
    const cart = this.cartSubject$.getValue();
    return cart.requestProducts.some((p) => p.id === product.customizableInfo?.id && p.designId !== null);
  }

  /**
   * Elimina un producto customizado del carrito
   * @param product El producto a eliminar
   */
  public removeProductCustomizedFromCart(product: Product) {
    const cart = this.cartSubject$.getValue();
    cart.requestProducts = cart.requestProducts.filter(
      (p) => !(p.id === product.customizableInfo?.id && p.designId !== null)
    );
    this.cartSubject$.next(cart);
  }

  /**
   * Elimina un producto customizado del carrito y añade la cantidad al producto sin customizar
   * @param product El producto a eliminar
   */
  public removeCustomizedFromCart(product: Product) {
    const cart = this.cartSubject$.getValue();
    const customizedProduct = cart.requestProducts.find((p) => p.id === product.id);
    const uncustomizedProduct = cart.requestProducts.find((p) => p.id === product.uncustomizedProduct.id);

    // Actualizamos cantidades del producto sin customizar
    if (uncustomizedProduct) {
      this.updateQtProductFromCart(uncustomizedProduct, uncustomizedProduct.qt + customizedProduct.qt).then();
    } else {
      this.addProductToCart(product.uncustomizedProduct, customizedProduct.qt);
    }

    // Eliminar el producto con customización del carrito
    cart.requestProducts = cart.requestProducts.filter((p) => p.id !== product.id);
    this.cartSubject$.next(cart);
  }

  public refreshCartCheck(): void {
    const cart = this.cartSubject$.getValue();
    this.cartClone = undefined;
    this.cartSubject$.next({ ...cart });
  }
}
